export const STOREFRONT_CONTRACT_VERSION = 15 as const;
export type StorefrontContractVersion = typeof STOREFRONT_CONTRACT_VERSION;

export const MENU_ITEM_SLUG_MAX_LENGTH = 160;

const lowercaseLatinOrAsciiDigit = /^[a-z0-9]$/;
const arabicScriptCharacter = /^\p{Script_Extensions=Arabic}$/u;
const letterNumberOrMark = /^[\p{Letter}\p{Number}\p{Mark}]$/u;

function isMenuItemSlugCharacter(value: string) {
  return (
    lowercaseLatinOrAsciiDigit.test(value) ||
    (arabicScriptCharacter.test(value) && letterNumberOrMark.test(value))
  );
}

export function normalizeMenuItemSlug(value: string) {
  let normalized = "";
  let needsSeparator = false;

  for (const character of value.normalize("NFKC").trim().toLowerCase()) {
    if (isMenuItemSlugCharacter(character)) {
      if (needsSeparator && normalized) normalized += "-";
      normalized += character;
      needsSeparator = false;
    } else if (normalized) {
      needsSeparator = true;
    }
  }

  return normalized;
}

export function isValidMenuItemSlug(value: string) {
  if (!value || value.length > MENU_ITEM_SLUG_MAX_LENGTH) return false;

  let segmentHasCharacters = false;
  for (const character of value) {
    if (character === "-") {
      if (!segmentHasCharacters) return false;
      segmentHasCharacters = false;
    } else {
      if (!isMenuItemSlugCharacter(character)) return false;
      segmentHasCharacters = true;
    }
  }

  return segmentHasCharacters;
}

export const ORDER_STATUSES = [
  "awaiting_payment",
  "pending",
  "accepted",
  "rejected",
  "cancelled",
  "completed",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];
export const TENANT_ORDER_TRANSITIONS: Readonly<
  Partial<Record<OrderStatus, readonly OrderStatus[]>>
> = {
  pending: ["accepted", "rejected"],
  accepted: ["completed"],
};
export type CustomerOrderCancellation = {
  id: string;
  status: "cancelled";
  cancelled_at: string;
  payment_status: string;
};

export const STOREFRONT_API_PATHS = {
  bootstrap: "/v1/commerce/bootstrap",
  branches: "/v1/commerce/branches",
  deliveryZones: "/v1/commerce/delivery-zones",
  menu: "/v1/commerce/menu",
  compactMenu: "/v1/commerce/menu-compact",
  deliveryCheck: "/v1/commerce/delivery/check",
  quote: "/v1/commerce/cart/quote",
  orders: "/v1/commerce/orders",
  customerRegister: "/v1/customer/register",
  customerLogin: "/v1/customer/login",
  customerRefresh: "/v1/customer/refresh",
  customerLogout: "/v1/customer/logout",
  customerProfile: "/v1/customer/profile",
  customerOtpStart: "/v1/customer/otp/start",
  customerOtpVerify: "/v1/customer/otp/verify",
  customerOtpComplete: "/v1/customer/otp/complete",
  customerAddresses: "/v1/customer/addresses",
  customerOrders: "/v1/customer/orders",
  customerLoyalty: "/v1/customer/loyalty",
} as const;

export const storefrontApiPath = {
  offerAttribution: (token: string) =>
    `/v1/commerce/offers/${encodeURIComponent(token)}/attribution` as const,
  product: (slug: string) =>
    `/v1/commerce/products/${encodeURIComponent(slug)}` as const,
  customerAddress: (addressId: string) =>
    `/v1/customer/addresses/${encodeURIComponent(addressId)}` as const,
  customerOrder: (orderId: string) =>
    `/v1/customer/orders/${encodeURIComponent(orderId)}` as const,
  customerOrderCancellation: (orderId: string) =>
    `/v1/customer/orders/${encodeURIComponent(orderId)}/cancel` as const,
  paymentAttempt: (attemptId: string) =>
    `/v1/commerce/payment-attempts/${encodeURIComponent(attemptId)}` as const,
  paymentAttemptReconciliation: (attemptId: string) =>
    `/v1/commerce/payment-attempts/${encodeURIComponent(attemptId)}/reconcile` as const,
  paymentAttemptRetry: (attemptId: string) =>
    `/v1/commerce/payment-attempts/${encodeURIComponent(attemptId)}/retry` as const,
  paymentLink: (token: string) =>
    `/v1/commerce/payment-links/${encodeURIComponent(token)}` as const,
} as const;

export type StorefrontApiPath =
  (typeof STOREFRONT_API_PATHS)[keyof typeof STOREFRONT_API_PATHS];
export type StorefrontApiEnvelope<T> = { data: T };
export type StorefrontApiErrorEnvelope = {
  error: {
    code: string;
    message: string;
    requestId?: string;
    details?: Record<string, unknown>;
  };
};

export const STOREFRONT_IDS = ["modern"] as const;
export type StorefrontId = (typeof STOREFRONT_IDS)[number];
export type StorefrontLifecycle =
  "active" | "unavailable" | "deprecated" | "disabled";
export type StorefrontMode = "published" | "preview" | "unpublished";
export type LocalizedText = Record<string, string>;
export type StorefrontConfiguration = Record<string, unknown>;

export type StorefrontPublicSettingsConfiguration = {
  policies: {
    privacy: LocalizedText;
    terms: LocalizedText;
    refund: LocalizedText;
  };
};

export const STOREFRONT_TRACKING_CONFIGURATION_KEYS = {
  googleTagId: "googleTagId",
  metaPixelId: "metaPixelId",
} as const;

export const GOOGLE_TAG_ID_PATTERN = /^(?:G|GT)-[A-Z0-9]{4,32}$/;
export const META_PIXEL_ID_PATTERN = /^\d{5,32}$/;

export function normalizeGoogleTagId(value: string): string {
  return value.trim().toUpperCase();
}

export function isGoogleTagId(value: string): boolean {
  return GOOGLE_TAG_ID_PATTERN.test(normalizeGoogleTagId(value));
}

export function isMetaPixelId(value: string): boolean {
  return META_PIXEL_ID_PATTERN.test(value.trim());
}

export type StorefrontTrackingConfiguration = {
  googleTagId: string | null;
  metaPixelId: string | null;
};

export type StorefrontImageVariant = {
  url: string;
  width: number;
  height: number;
  format: "webp";
};

export type StorefrontResponsiveImage = {
  src: string;
  width: number;
  height: number;
  variants: StorefrontImageVariant[];
};

export const STOREFRONT_FEATURE_KEYS = [
  "delivery",
  "pickup",
  "dine_in_catalogue",
  "asap_ordering",
  "scheduled_pickup",
  "scheduled_delivery",
  "customer_accounts",
  "phone_verification",
  "cash_on_delivery",
  "cash_on_pickup",
  "promotions",
  "loyalty",
  "order_notes",
] as const;
export type StorefrontFeatureKey = (typeof STOREFRONT_FEATURE_KEYS)[number];

export type StorefrontFeatureAvailability = {
  availableToRestaurant: boolean;
  supportedByStorefront: boolean;
  enabledForCustomers: boolean;
  reason?: string;
};

export type StorefrontAssetPurpose = "branding" | "storefront_hero";

export type ConfigurationField = {
  key: string;
  label: LocalizedText;
  description?: LocalizedText;
  control:
    | "color"
    | "short-text"
    | "localized-short-text"
    | "boolean"
    | "select"
    | "asset"
    | "asset-list";
  required: boolean;
  options?: Array<{ value: string; label: LocalizedText }>;
  maximumLength?: number;
  maximumItems?: number;
  assetPurpose?: StorefrontAssetPurpose;
};

export type StorefrontDefinitionMetadata = {
  id: StorefrontId;
  name: LocalizedText;
  description: LocalizedText;
  lifecycle: StorefrontLifecycle;
  rendererKey: StorefrontId;
  configurationSchemaVersion: number;
  fields: ConfigurationField[];
  defaultConfiguration: StorefrontConfiguration;
  capabilities: StorefrontFeatureKey[];
  supportedContractVersions: number[];
  available: boolean;
  availabilityReason?: string;
};

export type StorefrontVersionSummary = {
  id: string;
  versionNumber: number;
  storefrontId: StorefrontId;
  configurationSchemaVersion: number;
  contractVersion: number;
  sourceDraftRevision: number;
  restoredFromVersionId: string | null;
  publishedAt: string;
  publishedByAccountId: string;
};

export type StorefrontPublishedState = StorefrontVersionSummary & {
  rendererKey: StorefrontId;
  configuration: StorefrontConfiguration;
};

export type StorefrontDraftState = {
  storefrontId: StorefrontId;
  configurationSchemaVersion: number;
  configuration: StorefrontConfiguration;
  assetUrls?: Record<string, string>;
  revision: number;
  updatedAt: string;
};

export type StorefrontEdgeState = {
  defaultDomain: string;
  status: "queued" | "provisioning" | "active" | "failed" | "disabled";
  provisioningEnabled: boolean;
  lastSyncedAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
};

export type StorefrontManagementState = {
  draft: StorefrontDraftState;
  published: StorefrontPublishedState | null;
  edge: StorefrontEdgeState;
};

export type StorefrontDomainState =
  "pending" | "validating" | "active" | "failed" | "disconnecting";

export type StorefrontApexRedirect = {
  hostname: string;
  targetHostname: string;
  state: StorefrontDomainState;
  dnsStatus: "pending" | "verified" | "conflict";
  certificateStatus: "pending" | "issued";
  aRecord: { name: "@"; type: "A"; value: string };
  setupExpiresAt: string | null;
  lastCheckedAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
};

export type StorefrontDomain = {
  id: string;
  hostname: string;
  domainType: "emynor_subdomain" | "custom_subdomain";
  state: StorefrontDomainState;
  ownershipStatus: string;
  dnsStatus: string;
  certificateStatus: string;
  cloudfrontDomainStatus: string;
  isPrimary: boolean;
  cnameTarget: string;
  cname: { name: string; type: "CNAME"; target: string } | null;
  lastCheckedAt: string | null;
  verificationExpiresAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  apexRedirect?: StorefrontApexRedirect | null;
  apexRedirectAvailable?: boolean;
  apexRedirectSupported?: boolean;
};

export type StorefrontPreviewSession = {
  url: string;
  token: string;
  expiresAt: string;
  draftRevision: number;
};

export const STOREFRONT_LIVE_PREVIEW_UPDATE =
  "emynor:storefront-preview:update" as const;
export const STOREFRONT_LIVE_PREVIEW_READY =
  "emynor:storefront-preview:ready" as const;

export type StorefrontLivePreviewUpdate = {
  type: typeof STOREFRONT_LIVE_PREVIEW_UPDATE;
  payload: {
    configuration: StorefrontConfiguration;
    assetUrls: Record<string, string>;
    settingsConfiguration: StorefrontPublicSettingsConfiguration;
    tenant: {
      name: LocalizedText;
      enabledLocales: string[];
      logoUrl: string | null;
      faviconUrl: string | null;
      socialLinks?: StorefrontRestaurant["socialLinks"];
      supportPhone: string;
    };
  };
};

export type StorefrontLivePreviewReady = {
  type: typeof STOREFRONT_LIVE_PREVIEW_READY;
};

export type Money = { amount: number; currency: string };

export type StorefrontPaymentLink = {
  restaurant: {
    name: LocalizedText;
    logoUrl: string | null;
    supportPhone: string;
  };
  order: {
    number: string;
    customerName: string;
    amountMinor: number;
    currency: string;
    branchName: LocalizedText;
    orderType: "pickup" | "delivery";
    fulfillment: Fulfillment;
    address: {
      street: string;
      area: string;
      building: string;
      floor: string;
      apartment: string;
      landmark: string;
      instructions: string;
    } | null;
    notes: string | null;
    pricing: {
      subtotalMinor: number;
      discountMinor: number;
      deliveryFeeMinor: number;
      serviceFeeMinor: number;
      taxMinor: number;
      tipMinor: number;
    };
    items: Array<{
      name: LocalizedText;
      imageUrl: string | null;
      variantName: LocalizedText | null;
      quantity: number;
      unitPriceMinor: number;
      lineTotalMinor: number;
      notes: string | null;
      addons: Array<{ name: LocalizedText }>;
    }>;
  };
  status:
    | "awaiting_payment"
    | "paid"
    | "failed"
    | "expired"
    | "cancelled"
    | "review_required";
  expiresAt: string;
  checkout: {
    available: boolean;
    url: string | null;
    attemptId: string | null;
  };
};
export type ServiceType = "delivery" | "pickup" | "indoor";
export type Fulfillment =
  { mode: "asap" } | { mode: "scheduled"; scheduledFor: string };

export type StorefrontRestaurant = {
  id: string;
  slug: string;
  name: LocalizedText;
  currency: string;
  countryCode: string;
  timezone: string;
  defaultLocale: "en" | "ar";
  enabledLocales: string[];
  status: string;
  orderingPaused: boolean;
  logoUrl: string | null;
  faviconUrl: string | null;
  logoImage?: StorefrontResponsiveImage | null;
  faviconImage?: StorefrontResponsiveImage | null;
  supportPhone: string;
  canonicalHost?: string;
  seo?: { title: string; description: string; socialImageUrl: string | null };
  socialLinks?:
    | Partial<
        Record<
          "facebook" | "instagram" | "snapchat" | "youtube" | "tiktok",
          string
        >
      >
    | undefined;
};

export type StorefrontBootstrap = {
  contractVersion: StorefrontContractVersion;
  mode: StorefrontMode;
  tenant: StorefrontRestaurant;
  settings: {
    onlineOrderingEnabled: boolean;
    dineInEnabled: boolean;
    minimumOrder: Money;
    taxRate: number;
    pricesIncludeTax: boolean;
    paymentMethods: {
      cashDelivery: boolean;
      cashPickup: boolean;
      online: boolean;
    };
    configuration: StorefrontPublicSettingsConfiguration;
  } | null;
  storefront: {
    id: StorefrontId;
    rendererKey: StorefrontId;
    configurationSchemaVersion: number;
    configuration: StorefrontConfiguration;
    assetUrls: Record<string, string>;
    assetImages?: Record<string, StorefrontResponsiveImage>;
    version: StorefrontVersionSummary | null;
    capabilities: StorefrontFeatureKey[];
  } | null;
  features: Partial<
    Record<StorefrontFeatureKey, StorefrontFeatureAvailability>
  >;
  map: { tileUrl: string; attribution: string };
};

export type StorefrontBranch = {
  id: string;
  name_i18n: LocalizedText;
  address_i18n: LocalizedText;
  phone_numbers: string[];
  lat: number | null;
  lng: number | null;
  image_url: string | null;
  image?: StorefrontResponsiveImage | null;
  youtube_url: string | null;
  timezone: string;
  has_indoor: boolean;
  open_now: boolean;
  next_opening: { day_of_week: number; opens_at: string } | null;
  order_pause: { active: boolean; resumes_at: string | null };
  hours: Array<{
    branch_id: string;
    day_of_week: number;
    opens_at: string | null;
    closes_at: string | null;
    closed: boolean;
  }>;
};

export type StorefrontDeliveryZone = {
  id: string;
  name: string;
  boundary: Record<string, unknown>;
  center_lat: number;
  center_lng: number;
  delivery_fee_minor: number;
  minimum_order_minor: number;
  branch_ids: string[];
};

export type StorefrontAddon = {
  group_id: string;
  id: string;
  name: LocalizedText;
  priceMinor: number;
  available: boolean;
};

export type StorefrontAddonGroup = {
  id: string;
  name: LocalizedText;
  minimum: number;
  maximum: number;
  options: StorefrontAddon[];
};

export type StorefrontVariant = {
  id: string;
  menu_item_id: string;
  name: LocalizedText;
  priceMinor: number;
  originalPriceMinor?: number | null | undefined;
  image_url: string | null;
  image?: StorefrontResponsiveImage | null;
  available: boolean;
  addon_groups: StorefrontAddonGroup[];
};

export type StorefrontMenuItem = {
  id: string;
  category_id: string;
  name_i18n: LocalizedText;
  description_i18n: LocalizedText;
  slug: string;
  item_type: "simple" | "variable";
  price_minor: number;
  original_price_minor?: number | null | undefined;
  currency: string;
  featured: boolean;
  sort_order: number;
  image_url: string | null;
  image?: StorefrontResponsiveImage | null;
  variants: StorefrontVariant[];
  addon_groups: StorefrontAddonGroup[];
  for_online: boolean;
  for_indoor: boolean;
};

export type StorefrontCategory = {
  id: string;
  name_i18n: LocalizedText;
  description_i18n: LocalizedText;
  slug: string;
  sort_order: number;
  image_url: string | null;
  image?: StorefrontResponsiveImage | null;
};

export type StorefrontMenu = {
  nextSaleChangeAt?: string | null | undefined;
  categories: StorefrontCategory[];
  items: StorefrontMenuItem[];
  checkoutSuggestionItemIds?: string[];
};

export const STOREFRONT_COMPACT_MENU_VERSION = 4 as const;

export type StorefrontCompactAddonGroup = Omit<
  StorefrontAddonGroup,
  "options" | "minimum" | "maximum"
>;

export type StorefrontCompactAddonGroupAssignment = {
  groupId: string;
  minimum: number;
  maximum: number;
  optionIds: string[];
};

export type StorefrontCompactVariant = Omit<
  StorefrontVariant,
  "addon_groups"
> & {
  addonGroups: StorefrontCompactAddonGroupAssignment[];
};

export type StorefrontCompactMenuItem = Omit<
  StorefrontMenuItem,
  "variants" | "addon_groups"
> & {
  variants: StorefrontCompactVariant[];
  addonGroups: StorefrontCompactAddonGroupAssignment[];
};

export type StorefrontCompactMenu = {
  nextSaleChangeAt?: string | null | undefined;
  formatVersion: typeof STOREFRONT_COMPACT_MENU_VERSION;
  checkoutSuggestionItemIds?: string[];
  categories: StorefrontCategory[];
  addonGroups: StorefrontCompactAddonGroup[];
  addons: StorefrontAddon[];
  items: StorefrontCompactMenuItem[];
};

export function compactStorefrontMenu(
  menu: StorefrontMenu,
): StorefrontCompactMenu {
  const addonGroups = new Map<string, StorefrontCompactAddonGroup>();
  const addons = new Map<string, StorefrontAddon>();
  const groupReferences = (groups: StorefrontAddonGroup[]) =>
    groups.map((group) => {
      if (!addonGroups.has(group.id)) {
        addonGroups.set(group.id, {
          id: group.id,
          name: group.name,
        });
      }
      for (const option of group.options) {
        if (!addons.has(option.id)) addons.set(option.id, option);
      }
      return {
        groupId: group.id,
        minimum: group.minimum,
        maximum: group.maximum,
        optionIds: group.options.map((option) => option.id),
      };
    });

  const items = menu.items.map(({ addon_groups, variants, ...item }) => ({
    ...item,
    addonGroups: groupReferences(addon_groups),
    variants: variants.map(({ addon_groups: variantGroups, ...variant }) => ({
      ...variant,
      addonGroups: groupReferences(variantGroups),
    })),
  }));
  return {
    formatVersion: STOREFRONT_COMPACT_MENU_VERSION,
    checkoutSuggestionItemIds: menu.checkoutSuggestionItemIds ?? [],
    categories: menu.categories,
    addonGroups: [...addonGroups.values()],
    addons: [...addons.values()],
    items,
  };
}

export type StorefrontProductOrderabilityReason =
  | "selection_required"
  | "branch_unavailable"
  | "not_available_at_branch"
  | "not_available_for_order_type"
  | "no_available_variants";

export type StorefrontProductVariant = Omit<StorefrontVariant, "priceMinor"> & {
  priceMinor: number | null;
};

export type StorefrontProductItem = Omit<
  StorefrontMenuItem,
  "price_minor" | "variants"
> & {
  price_minor: number | null;
  variants: StorefrontProductVariant[];
};

export type StorefrontProduct = {
  nextSaleChangeAt?: string | null | undefined;
  item: StorefrontProductItem;
  orderability: {
    canAddToCart: boolean;
    reason: StorefrontProductOrderabilityReason | null;
  };
};

export type StorefrontQuoteRequest = {
  branchId: string;
  orderType: "delivery" | "pickup";
  lines: Array<{
    itemId: string;
    variantId?: string | null;
    addonIds: string[];
    quantity: number;
    notes?: string;
  }>;
  promotionCode?: string;
  promotionAttributionToken?: string;
  redeemLoyaltyReward?: boolean;
  redeemLoyaltyPoints?: number;
  location?: { lat: number; lng: number };
  fulfillment: Fulfillment;
};

export type StorefrontPromotionAttribution = {
  attributionToken: string;
  expiresAt: string;
  promotion: {
    id: string;
    name: LocalizedText;
  };
};

export type StorefrontQuote = {
  quoteId: string;
  expiresInSeconds: number;
  nextSaleChangeAt?: string | null;
  lines: Array<{
    itemId: string;
    name: LocalizedText;
    imageUrl: string | null;
    variantId: string | null;
    variantName: LocalizedText | null;
    categoryId: string;
    addons: Array<{ id: string; name: LocalizedText; priceMinor: number }>;
    quantity: number;
    baseUnitPriceMinor: number;
    addonUnitPriceMinor: number;
    unitPriceMinor: number;
    originalUnitPriceMinor?: number | null;
    lineTotalMinor: number;
    discountMinor: number;
    netTotalMinor: number;
    notes: string | null;
    sortOrder: number;
  }>;
  pricing: {
    subtotalMinor: number;
    merchandiseDiscountMinor: number;
    deliveryDiscountMinor: number;
    discountMinor: number;
    deliveryFeeBeforeDiscountMinor: number;
    deliveryFeeMinor: number;
    serviceFeeMinor: number;
    taxMinor: number;
    pricesIncludeTax: boolean;
    totalMinor: number;
    currency: string;
  };
  delivery: {
    zoneId: string;
    zoneName: string;
    location: { lat: number; lng: number };
    minimumOrderMinor: number;
  } | null;
  promotions: Array<{
    id: string;
    name: LocalizedText;
    version: number;
    type: "order_percentage" | "order_fixed" | "free_delivery";
    source: "link" | "code" | "automatic";
    code: string | null;
    merchandiseDiscountMinor: number;
    deliveryDiscountMinor: number;
    savingsMinor: number;
    lineDiscounts: Array<{ lineKey: string; discountMinor: number }>;
  }>;
  promotionAttributionToken: string | null;
  promotionAttributionExpiresAt: string | null;
  loyalty: StorefrontLoyaltyQuote;
  fulfillment: Fulfillment;
};

export type LoyaltyLedgerEntryKind =
  "earned" | "held" | "redeemed" | "released" | "adjustment";

export type StorefrontLoyaltyProgram = {
  status: "active" | "paused";
  currency: string;
  pointsPerCurrencyUnit: number;
  rewardPoints: number;
  rewardDiscountMinor: number;
};

export type StorefrontLoyaltyQuote = {
  program: StorefrontLoyaltyProgram | null;
  availablePoints: number | null;
  heldPoints: number | null;
  expectedPoints: number;
  rewardEligible: boolean;
  minimumRedeemablePoints: number;
  maximumRedeemablePoints: number;
  applied: {
    pointCost: number;
    discountMinor: number;
  } | null;
};

export type StorefrontLoyaltyWallet = {
  program: StorefrontLoyaltyProgram | null;
  account: {
    availablePoints: number;
    heldPoints: number;
    rewardEligible: boolean;
    pointsToReward: number;
  };
  activity: Array<{
    id: string;
    kind: LoyaltyLedgerEntryKind;
    availableDeltaPoints: number;
    heldDeltaPoints: number;
    availableBalanceAfter: number;
    heldBalanceAfter: number;
    orderId: string | null;
    reason: string | null;
    createdAt: string;
  }>;
};

export type DeliveryCheck =
  | { deliverable: false; reason: string }
  | {
      deliverable: true;
      branchId: string;
      fee: Money;
      minimumOrder: Money;
      zone: {
        id: string;
        name: string;
        boundary: Record<string, unknown>;
        center: { lat: number; lng: number };
      };
    };

export type CustomerProfile = {
  id: string;
  name: string;
  phone_e164: string | null;
  phone_verified_at?: string | null;
  phone_verification_method?: string | null;
  created_at?: string;
  version?: number;
};

export type CustomerAuthSession = {
  accessToken: string;
  expiresIn: number;
};

export type CustomerRegisterRequest = {
  name: string;
  phone: string;
  password: string;
};

export type CustomerLoginRequest = {
  identifier: string;
  password: string;
};

export type CustomerOtpStartRequest = { phone: string };
export type CustomerOtpStartResponse = {
  challengeId: string;
  delivery: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
};

export type CustomerOtpVerifyRequest = {
  challengeId: string;
  phone: string;
  code: string;
  promotionAttributionToken?: string;
};

export type CustomerOtpVerifyResponse =
  | (CustomerAuthSession & {
      status: "authenticated";
      customer: CustomerProfile;
    })
  | {
      status: "registration_required";
      registrationToken: string;
      expiresInSeconds: number;
    };

export type CustomerOtpCompleteRequest = {
  challengeId: string;
  registrationToken: string;
  name: string;
  promotionAttributionToken?: string;
};

export type CustomerOtpCompleteResponse = CustomerAuthSession & {
  customer: CustomerProfile;
};

export type CustomerProfileUpdateRequest = {
  name: string;
};

export type CustomerLogoutResponse = { loggedOut: true };

export type CustomerAddressInput = {
  label: LocalizedText;
  street: string;
  area: string;
  building: string;
  floor: string;
  apartment: string;
  landmark: string;
  instructions: string;
  location: { lat: number; lng: number };
};

export type CustomerAddress = Omit<
  CustomerAddressInput,
  "label" | "location"
> & {
  id: string;
  label_i18n: LocalizedText;
  lat: number;
  lng: number;
  version: number;
};

export type StorefrontPaymentMethod =
  "cash_delivery" | "cash_pickup" | "online";

type StorefrontOrderRequestBase = {
  quoteId: string;
  locale?: "en" | "ar";
  contact: {
    name: string;
    phone: string;
  };
  address?: Record<string, unknown>;
  notes?: string;
};

export type StorefrontOrderRequest = StorefrontOrderRequestBase &
  (
    | { paymentMethod: "cash_delivery" | "cash_pickup" }
    | { paymentMethod: "online"; idempotencyKey: string }
  );

export type StorefrontOrder = {
  id: string;
  orderNumber: string;
  status: string;
  paymentStatus: "unpaid" | "paid" | string;
  orderType: "delivery" | "pickup";
  fulfillment: Fulfillment;
  pricing: StorefrontQuote["pricing"];
  promotions: StorefrontQuote["promotions"];
  loyalty: {
    reward: {
      pointCost: number;
      discountMinor: number;
      status: "held" | "redeemed" | "released";
    } | null;
    pointsEarned: number;
  } | null;
  createdAt: string;
};

export type StorefrontPaymentAttemptStatus =
  | "queued"
  | "initializing"
  | "requires_customer_action"
  | "processing"
  | "unknown"
  | "succeeded"
  | "failed"
  | "expired"
  | "cancellation_pending"
  | "cancelled";

export type StorefrontPaymentAttempt = {
  id: string;
  orderId: string;
  intentId: string;
  providerKey: "fawry";
  status: StorefrontPaymentAttemptStatus;
  attemptNumber: number;
  nextAction: {
    type: "redirect";
    url: string;
    expiresAt: string;
  } | null;
  safeFailureCode: string | null;
  createdAt: string;
  updatedAt: string;
};

export type StorefrontOnlineOrder = {
  order: StorefrontOrder & {
    status: "awaiting_payment";
    paymentStatus: "processing";
  };
  paymentAttempt: Pick<
    StorefrontPaymentAttempt,
    "id" | "status" | "nextAction"
  >;
};

export type StorefrontCreateOrderResponse =
  StorefrontOrder | StorefrontOnlineOrder;

export type StorefrontOrderPaymentSummary = {
  method: "cash" | "online";
  status:
    | "unpaid"
    | "processing"
    | "paid"
    | "partially_refunded"
    | "refunded"
    | "review_required"
    | "expired";
  attemptId: string | null;
  attemptStatus: StorefrontPaymentAttemptStatus | null;
  expectedAmountMinor: number;
  customerPaidAmountMinor: number | null;
  refundedAmountMinor: number;
  currency: string;
  updatedAt: string | null;
};

export type StorefrontOrderPromotion = {
  id: string;
  name_i18n: LocalizedText;
  promotion_type: StorefrontQuote["promotions"][number]["type"];
  claim_source: "link" | "code" | "automatic";
  code: string | null;
  merchandise_discount_minor: number;
  delivery_discount_minor: number;
  discount_minor: number;
};

export type StorefrontOrderHistoryItem = {
  id: string;
  order_number: string;
  status: string;
  payment_status: string;
  payment_method: StorefrontPaymentMethod;
  order_type: "delivery" | "pickup";
  currency: string;
  subtotal_minor: number;
  discount_minor: number;
  delivery_fee_minor: number;
  service_fee_minor: number;
  tax_minor: number;
  total_minor: number;
  fulfillment_mode: "asap" | "scheduled";
  scheduled_for: string | null;
  requested_at: string;
  promotions: StorefrontOrderPromotion[];
  loyalty: StorefrontOrder["loyalty"];
};

export type StorefrontOrderHistoryDetail = StorefrontOrderHistoryItem & {
  payment_summary: StorefrontOrderPaymentSummary;
  history: Array<{
    id: string;
    from_status: string | null;
    to_status: string;
    created_at: string;
  }>;
  branch_id: string;
  customer_name: string;
  customer_phone_e164: string;
  customer_email: string | null;
  delivery_address_json: Record<string, unknown> | null;
  tip_minor: number;
  customer_notes: string | null;
  rejection_reason: string | null;
  accepted_at: string | null;
  ready_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  version: number;
  items: Array<{
    id: string;
    menu_item_id: string | null;
    menu_item_name_i18n: LocalizedText;
    image_url: string | null;
    variant_id: string | null;
    variant_label_i18n: LocalizedText | null;
    quantity: number;
    unit_price_minor: number;
    original_unit_price_minor?: number | null;
    line_total_minor: number;
    discount_minor: number;
    net_total_minor: number;
    notes: string | null;
    sort_order: number;
    addons: Array<{
      id: string;
      addon_id: string | null;
      addon_name_i18n: LocalizedText;
      price_minor: number;
    }>;
  }>;
};
